import path from 'node:path';
import { callLlmJson } from './llm-client.mjs';
import { readJson } from './json-utils.mjs';
import { CONFIG_DIR } from './repo-paths.mjs';

let configCache = null;

export function nonHumanoidMotionConfig() {
    if (!configCache) configCache = readJson(path.join(CONFIG_DIR, 'non-humanoid-motion.json'));
    return configCache;
}

function sourceTextFor(entry, maxCharacters) {
    return [
        entry?.nameJa,
        entry?.nameEn,
        entry?.promptEn,
        entry?.appearance,
        JSON.stringify(entry?.visualProfile ?? {}),
    ].filter(Boolean).join('\n').slice(0, maxCharacters).toLowerCase();
}

export function classifyNonHumanoidByKeywords(entry, config = nonHumanoidMotionConfig()) {
    const text = sourceTextFor(entry, config.classification.maxPromptCharacters);
    for (const rule of config.classification.keywordRules) {
        if (rule.keywords.some((keyword) => text.includes(String(keyword).toLowerCase()))) {
            return { category: rule.category, confidence: 0.72, source: 'keyword' };
        }
    }
    return { category: config.fallbackCategory, confidence: 0, source: 'fallback' };
}

export function buildNonHumanoidClassificationPrompt(entry, config = nonHumanoidMotionConfig()) {
    return [
        'ゲーム用の非人型モンスターを、外形と自然な移動方法に基づいて分類してください。',
        `許可カテゴリ: ${config.classification.categories.join(', ')}`,
        `名前: ${entry?.nameJa ?? entry?.castId ?? ''}`,
        `外見記述: ${sourceTextFor(entry, config.classification.maxPromptCharacters)}`,
        '骨格やMeshyアニメーションは使いません。静的GLBへゲーム側の手続き型モーションを適用するための分類です。',
        '判断不能なら unknown を選んでください。',
        '出力形式（JSONのみ）: {"category":"<許可カテゴリ>","confidence":0.0から1.0,"reason":"短い理由"}',
    ].join('\n');
}

export function createNonHumanoidMotionProfile(classification, config = nonHumanoidMotionConfig()) {
    const allowed = new Set(config.classification.categories);
    const requestedCategory = allowed.has(classification?.category) ? classification.category : config.fallbackCategory;
    const confidence = Math.min(1, Math.max(0, Number(classification?.confidence) || 0));
    const category = confidence >= config.confidenceThreshold ? requestedCategory : config.fallbackCategory;
    const selected = config.profiles[category] ?? config.profiles[config.fallbackCategory];
    return {
        formatVersion: config.formatVersion,
        enabled: true,
        category,
        classifiedCategory: requestedCategory,
        confidence,
        classificationSource: classification?.source ?? 'unknown',
        locomotion: selected.locomotion,
        attackMotion: selected.attackMotion,
        canonical: { ...config.canonical },
        parameters: { ...selected.parameters },
        attack: { ...selected.attack },
    };
}

export async function generateNonHumanoidMotionProfile(entry, { onLog = () => {} } = {}) {
    const config = nonHumanoidMotionConfig();
    let classification = null;
    try {
        const response = await callLlmJson('imagePrompt', {
            prompt: buildNonHumanoidClassificationPrompt(entry, config),
            maxTokens: 1024,
            onLog,
        });
        const category = String(response.json?.category ?? '');
        const confidence = Number(response.json?.confidence);
        if (config.classification.categories.includes(category) && Number.isFinite(confidence)) {
            classification = { category, confidence, source: 'llm' };
        }
    } catch (error) {
        onLog(`    （非人型モーション分類 LLM が失敗したためキーワード分類を使用: ${String(error?.message ?? error).slice(0, 80)}）`);
    }
    if (!classification) classification = classifyNonHumanoidByKeywords(entry, config);
    const profile = createNonHumanoidMotionProfile(classification, config);
    onLog(`    モーション分類: ${profile.category} / ${profile.locomotion} / ${profile.attackMotion}（${profile.classificationSource}, confidence=${profile.confidence.toFixed(2)}）`);
    return profile;
}
